import React from 'react';
import moment from 'moment';
import cn from 'classnames';

import { useShareAnalyticsQuery } from 'src/api/queries/shareQueries';
import { ShareRecentActivity, ShareAsset } from 'src/types/TShare';
import Shimmer from 'src/components/Shimmer';
import { Eye, DownloadSimple } from 'src/common/icons';
import { getODIThumbnailUrl } from 'src/utils/previews';
import { planUpgradeRequired } from 'src/api/utils/errorHandlers';
// Direct import, not the components barrel — this file is itself exported
// from the barrel, so importing it back would be circular.
import { PlanUpgradeBanner } from 'src/components/FeatureUnavailable/PlanUpgradeBanner';

import styles from './ShareAnalyticsPanel.module.scss';

interface ShareAnalyticsPanelProps {
  shareId: string;
  assets?: ShareAsset[];
}

export const ShareAnalyticsPanel: React.FC<ShareAnalyticsPanelProps> = ({ shareId, assets = [] }) => {
  // retry: false — a plan-gate 403 is deterministic; retrying it three times
  // only delays the upgrade banner.
  const { data: analyticsResult, isLoading, error } = useShareAnalyticsQuery(
    shareId, undefined, undefined, { retry: false },
  );

  // Share analytics is plan-gated (SHARE_ANALYTICS, Starter+; rollout 32).
  // The backend answers the structured plan_upgrade_required 403 — render
  // the standard upgrade banner in place of the dashboard. Data capture is
  // never gated, so the history is waiting after an upgrade.
  const gate = planUpgradeRequired(error);
  if (gate) {
    return <PlanUpgradeBanner gate={gate} />;
  }

  const analytics = analyticsResult?.data;
  const recentActivity = analytics?.recent_activity || [];
  const downloadsByKey = analytics?.downloads_by_key || {};
  const downloadsByAssetKey = analytics?.downloads_by_asset_key || {};

  // Calculate totals from events_by_type
  const totalViews = analytics?.events_by_type?.VIEW || 0;
  const totalDownloads = analytics?.events_by_type?.DOWNLOAD || 0;

  const formatDate = (dateString: string): string => {
    return moment(dateString).format('MMM D, YYYY h:mm A');
  };

  // Format download key for display (e.g., "480p-video" -> "480p Video", "odi-instagram" -> "ODI Instagram")
  const formatDownloadKey = (key: string | null | undefined): string => {
    if (!key) return '-';
    return key
      .split('-')
      .map(part => {
        // Keep "odi" as uppercase "ODI"
        if (part.toLowerCase() === 'odi') return 'ODI';
        return part.charAt(0).toUpperCase() + part.slice(1);
      })
      .join(' ');
  };

  // Get asset name by ID, fallback to truncated ID
  const getAssetName = (assetId: string): string => {
    const asset = assets.find(a => a.id === assetId);
    if (asset?.name) return asset.name;
    return `${assetId.substring(0, 8)}...`;
  };

  // Get asset thumbnail URL
  const getAssetThumbnail = (assetId: string): string | null => {
    const asset = assets.find(a => a.id === assetId);
    if (asset?.shared_links?.odi) {
      return getODIThumbnailUrl({ odi: asset.shared_links.odi, width: 40, height: 40 });
    }
    return null;
  };

  const hasDownloads = Object.keys(downloadsByKey).length > 0;
  const hasAssetDownloads = Object.keys(downloadsByAssetKey).length > 0;

  if (isLoading) {
    return (
      <div className={styles.panel}>
        <div className={styles.loadingState}>
          <Shimmer w="100%" h="100px" radius="8px" />
          <Shimmer w="100%" h="200px" radius="8px" />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.panel}>
      {/* Stats Summary */}
      <div className={styles.statsRow}>
        <div className={styles.statCard}>
          <Eye className={styles.statIcon} />
          <div className={styles.statInfo}>
            <span className={styles.statValue}>{totalViews}</span>
            <span className={styles.statLabel}>Views</span>
          </div>
        </div>

        <div className={styles.statCard}>
          <DownloadSimple className={styles.statIcon} />
          <div className={styles.statInfo}>
            <span className={styles.statValue}>{totalDownloads}</span>
            <span className={styles.statLabel}>Downloads</span>
          </div>
        </div>
      </div>

      {/* Downloads by Type */}
      {hasDownloads && (
        <div className={styles.downloadSection}>
          <h3 className={styles.sectionTitle}>Downloads by Type</h3>
          <div className={styles.downloadGrid}>
            {Object.entries(downloadsByKey).map(([key, count]) => (
              <div key={key} className={styles.downloadItem}>
                <span className={styles.downloadKey}>{formatDownloadKey(key)}</span>
                <span className={styles.downloadCount}>{count}</span>
          </div>
            ))}
          </div>
        </div>
      )}

      {/* Downloads by Asset */}
      {hasAssetDownloads && (
        <div className={styles.downloadSection}>
          <h3 className={styles.sectionTitle}>Downloads by Asset</h3>
          <div className={styles.assetDownloadsTable}>
            <div className={styles.assetTableHeader}>
              <span className={styles.colThumbnail}></span>
              <span className={styles.colAsset}>Asset</span>
              <span className={styles.colDownloads}>Downloads</span>
      </div>
            <div className={styles.assetTableBody}>
              {Object.entries(downloadsByAssetKey).map(([assetId, keys]) => {
                const thumbnail = getAssetThumbnail(assetId);
                const assetName = getAssetName(assetId);
                return (
                  <div key={assetId} className={styles.assetTableRow}>
                    <span className={styles.colThumbnail}>
                      {thumbnail && (
                        <a
                          href={`/asset/${assetId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={styles.thumbnailLink}
                        >
                          <img 
                            src={thumbnail} 
                            alt="" 
                            className={styles.assetThumbnail}
                          />
                        </a>
                      )}
                    </span>
                    <span className={styles.colAsset}>
                      <span className={styles.assetName} title={assetName}>{assetName}</span>
                      <span className={styles.assetId}>{assetId}</span>
                    </span>
                    <span className={styles.colDownloads}>
                      {Object.entries(keys).map(([downloadKey, count]) => (
                        <span key={downloadKey} className={styles.downloadBadge}>
                          {formatDownloadKey(downloadKey)}: {count}
                        </span>
                      ))}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
          </div>
        )}

      {/* Recent Activity Table */}
      <div className={styles.logsSection}>
        <h3 className={styles.sectionTitle}>
          Recent Download Activity (only latest 100 shown)
        </h3>
        
        {recentActivity.length === 0 ? (
          <div className={styles.emptyLogs}>
            <p>No activity recorded yet.</p>
          </div>
        ) : (
          <div className={styles.logsTable}>
            <div className={styles.tableHeader}>
              <span className={styles.colTimestamp}>Timestamp</span>
              <span className={styles.colType}>Event</span>
              <span className={styles.colDetails}>Details</span>
            </div>
            <div className={styles.tableBody}>
              {recentActivity.slice(0, 100).map((activity: ShareRecentActivity, index: number) => (
                <div key={index} className={styles.tableRow}>
                  <span className={styles.colTimestamp}>
                    {formatDate(activity.accessed_at)}
                  </span>
                  <span className={cn(styles.colType, styles[`event${activity.event_type}`])}>
                    {activity.event_type}
                  </span>
                  <span className={styles.colDetails}>
                    {activity.event_type === 'DOWNLOAD' && activity.download_key
                      ? `${activity.asset_id ? getAssetName(activity.asset_id) : '-'} / ${formatDownloadKey(activity.download_key)}`
                      : '-'}
                  </span>
                </div>
              ))}
            </div>
            {recentActivity.length > 100 && (
              <div className={styles.moreRows}>
                Showing first 100 of {recentActivity.length} entries
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ShareAnalyticsPanel;
